import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Nav from "../Components/Nav";
import Footer from "../Components/Footer";
import { getAuth } from "firebase/auth";
import { db } from "../config";
import { doc, getDoc, updateDoc, addDoc, collection } from "firebase/firestore";
import axios from "axios";

function Withdrawal() {
  const [walletAddress, setWalletAddress] = useState("");
  const [cryptoType, setCryptoType] = useState("BTC");
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState(null);
  const [balance, setBalance] = useState(0);
  const [withdrawalLimit, setWithdrawalLimit] = useState(null); // null = unlimited
  const [withdrawalFeePercentage, setWithdrawalFeePercentage] = useState(0);
  const [withdrawalFee, setWithdrawalFee] = useState(0);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const cryptoOptions = ["BTC", "ETH", "USDT", "TRON", "LTC"];
  const MIN_WITHDRAWAL = 1;

  // 🎨 Helper: Format limit for display
  const formatLimit = (limit) => {
    if (limit === null || limit === undefined || limit === Infinity) return "Unlimited";
    return `$${Number(limit).toLocaleString()}`;
  };

  // 🎨 Helper: Get effective max withdrawal (user limit or unlimited)
  const getMaxWithdrawal = () => {
    if (withdrawalLimit === null || withdrawalLimit === undefined || withdrawalLimit === Infinity) {
      return Infinity;
    }
    return Number(withdrawalLimit);
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          const userDocRef = doc(db, "users", firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            setBalance(data.balance || 0);
            setUsername(data.fullname || firebaseUser.email);
            // ✅ Treat null/undefined/Infinity as unlimited
            const limit = data.withdrawalLimit;
            setWithdrawalLimit(
              limit === null || limit === undefined || limit === Infinity ? null : Number(limit)
            );
            setWithdrawalFeePercentage(data.withdrawalFeePercentage ?? 0);
          }
        } catch (error) {
          console.error("Error fetching user ", error);
          toast.error("Failed to load account data");
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        navigate("/auth/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleWithdrawal = async (e) => {
    e.preventDefault();
    setMessage("");
    
    if (!user) {
      toast.error("Please log in to withdraw");
      return;
    }
    
    if (!walletAddress?.trim()) {
      toast.error("Please enter a wallet address");
      return;
    }
    
    if (!amount) {
      toast.error("Please enter an amount");
      return;
    }

    const withdrawalAmount = parseFloat(amount);
    
    if (isNaN(withdrawalAmount) || withdrawalAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    if (withdrawalAmount < MIN_WITHDRAWAL) {
      toast.error(`Minimum withdrawal is $${MIN_WITHDRAWAL}`);
      return;
    }
    
    if (withdrawalAmount > balance) {
      toast.error("Insufficient balance for this withdrawal");
      return;
    }
    
    // ✅ Check against user's withdrawal limit (if set)
    const maxWithdrawal = getMaxWithdrawal();
    if (maxWithdrawal !== Infinity && withdrawalAmount > maxWithdrawal) {
      toast.error(`Amount exceeds your withdrawal limit of ${formatLimit(withdrawalLimit)}`);
      return;
    }

    // Calculate fee
    const fee = (withdrawalFeePercentage / 100) * withdrawalAmount;
    setWithdrawalFee(fee);

    // Show fee confirmation if applicable
    if (withdrawalFeePercentage > 0) {
      setPendingWithdrawal({
        amount: withdrawalAmount,
        walletAddress: walletAddress.trim(),
        cryptoType,
      });
      setShowFeeModal(true);
    } else {
      await confirmWithdrawal({
        amount: withdrawalAmount,
        walletAddress: walletAddress.trim(),
        cryptoType,
      });
    }
  };

  const confirmWithdrawal = async (data = pendingWithdrawal) => {
    setShowFeeModal(false);
    
    if (!data?.amount || !data?.walletAddress || !data?.cryptoType) {
      toast.error("Withdrawal data is incomplete");
      return;
    }

    setSubmitting(true);

    try {
      const amount = parseFloat(data.amount);
      const { walletAddress, cryptoType } = data;

      // ✅ Create withdrawal request in Firestore
      await addDoc(collection(db, "withdrawals"), {
        userId: user.uid,
        email: user.email,
        username: username,
        amount,
        fee: withdrawalFee,
        total: amount + withdrawalFee,
        walletAddress,
        cryptoType,
        status: "pending",
        createdAt: new Date(),
        processedAt: null,
        txHash: null,
      });

      // ✅ Deduct from user balance immediately (adjust if you prefer pending deduction)
      const userRef = doc(db, "users", user.uid);
      const newBalance = balance - amount;
      await updateDoc(userRef, { balance: newBalance });
      setBalance(newBalance);

      toast.success("Withdrawal request submitted successfully!");
      
      // Reset form
      setAmount("");
      setWalletAddress("");
      setPendingWithdrawal(null);

      // ✅ Send confirmation email
      await axios.post("https://renderbackend-4t2p.onrender.com/send-mail", {
        email: user.email,
        subject: "Withdrawal Request Received - APEXYEILD",
        CompanyName: "APEXYEILD",
        htmlToSend: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px; background-color: #f9f9f9;">
            <div style="text-align: center; margin-bottom: 20px;">
              <img src="https://apexyeild.com/public/uploads/logo/light.png" alt="APEXYEILD Logo" style="height: 60px;" />
              <h2 style="margin: 10px 0; color: #1a1a1a;">APEXYEILD</h2>
            </div>
            <h3 style="color: #333;">Hi ${username}!</h3>
            <p style="color: #555;">Your withdrawal request has been received:</p>
            <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
              <tr><td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Cryptocurrency:</strong></td><td style="padding: 8px; border-bottom: 1px solid #ddd;">${cryptoType}</td></tr>
              <tr><td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Amount:</strong></td><td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong style="color: #212529;">$${amount.toFixed(2)}</strong></td></tr>
              <tr><td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Processing Fee:</strong></td><td style="padding: 8px; border-bottom: 1px solid #ddd;">$${withdrawalFee.toFixed(2)}</td></tr>
              <tr><td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Wallet Address:</strong></td><td style="padding: 8px; border-bottom: 1px solid #ddd; word-break: break-all; font-family: monospace;">${walletAddress}</td></tr>
              <tr><td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Status:</strong></td><td style="padding: 8px; border-bottom: 1px solid #ddd;"><span style="color: #f59e0b; font-weight: 600;">Pending Review</span></td></tr>
            </table>
            <p style="color: #555; margin-top: 20px;">Our team will review your request and process the withdrawal within 24-48 hours. You'll receive a confirmation email with the transaction hash once completed.</p>
            <p style="color: #555; margin-top: 20px;">Thank you for using APEXYEILD!</p>
            <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />
            <p style="font-size: 12px; color: #aaa; text-align: center;">© ${new Date().getFullYear()} APEXYEILD. All rights reserved.</p>
          </div>
        `,
      });

    } catch (err) {
      console.error("Withdrawal error:", err);
      toast.error("Failed to submit withdrawal. Please try again.");
      
      // Optional: Revert balance if deduction happened before error
      // await updateDoc(doc(db, "users", user.uid), { balance: balance });
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = async () => {
    if (!walletAddress) {
      toast.info("No wallet address to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(walletAddress);
      toast.success("Wallet address copied!", { autoClose: 1500 });
    } catch (err) {
      console.error("Copy failed:", err);
      toast.error("Failed to copy address");
    }
  };

  // if (loading) {
  //   return (
  //     <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
  //       <div className="text-center">
  //         <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }} />
  //         <p className="mt-3 text-muted">Loading withdrawal form...</p>
  //       </div>
  //     </div>
  //   );
  // }

  const maxWithdrawal = getMaxWithdrawal();
  const displayMax = formatLimit(withdrawalLimit);

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>APEXYEILD | Withdrawal</title>
        <link rel="icon" href="https://apexyeild.com/auth/storage/app/public/photos/IQAXeGtikmark.png1744508352" type="image/png" />
      </Helmet>

      <style>{`
        .custom-select {
          display: inline-block;
          max-width: 100%;
          height: calc(2.25rem + 2px);
          padding: .375rem 1.75rem .375rem .75rem;
          line-height: 1.5;
          color: #0b57e3;
          vertical-align: middle;
          background: #fff url("image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23333' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E") no-repeat right .75rem center;
          background-size: 8px 10px;
          border: 2px solid #dee2e6;
          border-radius: 10px;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          font-size: 0.95rem;
        }
        .custom-select:focus {
          border-color: #0b57e3;
          outline: 0;
          box-shadow: 0 0 0 3px rgba(11, 87, 227, 0.15);
        }
        .form-control {
          border-radius: 10px;
          border: 2px solid #dee2e6;
          padding: 0.85rem 1rem;
          font-size: 0.95rem;
          transition: all 0.2s ease;
        }
        .form-control:focus {
          border-color: #0b57e3;
          box-shadow: 0 0 0 3px rgba(11, 87, 227, 0.15);
        }
        .wallet-address {
          word-break: break-all;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 0.85rem;
          background: #f8fafc;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          border: 1px dashed #cbd5e1;
          color: #1e293b;
        }
        .limit-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.25rem 0.75rem;
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          border-radius: 50px;
          font-size: 0.8rem;
          font-weight: 500;
        }
        .limit-badge.unlimited {
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
        }
        .modal-content {
          border-radius: 16px;
          border: none;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .modal-header {
          border-bottom: 1px solid #e2e8f0;
          padding: 1.25rem 1.5rem;
        }
        .modal-body {
          padding: 1.5rem;
          font-size: 0.95rem;
          line-height: 1.6;
        }
        .modal-footer {
          border-top: 1px solid #e2e8f0;
          padding: 1rem 1.5rem;
          gap: 0.75rem;
        }
        .btn-close {
          filter: none;
          opacity: 0.6;
        }
        .btn-close:hover {
          opacity: 1;
        }
      `}</style>

      <ToastContainer
        position="top-right"
        autoClose={2500}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <div className="wrapper">
        <Nav />
        <div className="main-panel bg-light">
          <div className="content bg-light">
            <div className="page-inner">
              
              {/* Header */}
              <div className="mb-4 pb-2 border-bottom">
                <h1 className="title1 text-dark fw-bold mb-1">Withdraw Funds</h1>
                <p className="text-muted mb-0">
                  Available Balance: <span className="fw-semibold text-dark">${balance.toFixed(2)}</span>
                </p>
              </div>

              {/* Withdrawal Form Card */}
              <div className="row">
                <div className="col-lg-8 mx-auto">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: "16px" }}>
                    <div className="card-body p-4 p-md-5">
                      <form onSubmit={handleWithdrawal}>
                        
                        {/* Crypto Selection */}
                        <div className="mb-4">
                          <label className="form-label fw-semibold text-dark mb-2">
                            Cryptocurrency
                          </label>
                          <select
                            className="custom-select w-100"
                            value={cryptoType}
                            onChange={(e) => setCryptoType(e.target.value)}
                            required
                          >
                            {cryptoOptions.map((option) => (
                              <option key={option} value={option}>
                                {option} {option === "USDT" && "(TRC-20)"}
                              </option>
                            ))}
                          </select>
                          <small className="text-muted d-block mt-1">
                            Select the cryptocurrency for your withdrawal
                          </small>
                        </div>

                        {/* Wallet Address */}
                        <div className="mb-4">
                          <label className="form-label fw-semibold text-dark mb-2">
                            Wallet Address <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <input
                              className="form-control"
                              placeholder={`Enter your ${cryptoType} wallet address`}
                              type="text"
                              value={walletAddress}
                              onChange={(e) => setWalletAddress(e.target.value)}
                              required
                              autoComplete="off"
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={copyToClipboard}
                              title="Paste from clipboard"
                            >
                              <i className="fas fa-paste"></i>
                            </button>
                          </div>
                          {walletAddress && (
                            <div className="mt-2">
                              <small className="text-muted d-block mb-1">Entered address:</small>
                              <div className="wallet-address">{walletAddress}</div>
                            </div>
                          )}
                        </div>

                        {/* Amount Input */}
                        <div className="mb-4">
                          <label className="form-label fw-semibold text-dark mb-2">
                            Withdrawal Amount (USD)
                          </label>
                          <div className="input-group">
                            <span className="input-group-text">$</span>
                            <input
                              className="form-control"
                              placeholder={`Min: $${MIN_WITHDRAWAL}`}
                              type="number"
                              value={amount}
                              onChange={(e) => setAmount(e.target.value)}
                              required
                              min={MIN_WITHDRAWAL}
                              max={maxWithdrawal === Infinity ? undefined : maxWithdrawal}
                              step="0.01"
                            />
                          </div>
                          
                          {/* Limits & Fee Info */}
                          <div className="d-flex flex-wrap gap-2 mt-2">
                            <span className="limit-badge">
                              <i className="fas fa-arrow-down"></i> Min: ${MIN_WITHDRAWAL}
                            </span>
                            <span className={`limit-badge ${withdrawalLimit === null ? 'unlimited' : ''}`}>
                              <i className="fas fa-arrow-up"></i> Max: Unlimited
                            </span>
                            {withdrawalFeePercentage > 0 && (
                              <span className="limit-badge" style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)" }}>
                                <i className="fas fa-percent"></i> Fee: {withdrawalFeePercentage}%
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Balance Warning */}
                        {amount && parseFloat(amount) > balance && (
                          <div className="alert alert-warning small py-2 mb-3">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Amount exceeds your available balance of ${balance.toFixed(2)}
                          </div>
                        )}

                        {/* Submit Button */}
                        <div className="d-grid">
                          <button
                            type="submit"
                            className="btn btn-primary btn-lg py-3"
                            disabled={submitting || !amount || !walletAddress}
                            style={{
                              background: "linear-gradient(135deg, #0b57e3, #0846b8)",
                              border: "none",
                              borderRadius: "12px",
                              fontWeight: 500,
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-2px)";
                              e.currentTarget.style.boxShadow = "0 8px 25px rgba(11, 87, 227, 0.4)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "none";
                            }}
                          >
                            {submitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Processing...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-paper-plane me-2"></i>
                                Request Withdrawal
                              </>
                            )}
                          </button>
                        </div>

                        {/* Info Note */}
                        <div className="alert alert-info small mt-4 mb-0" style={{ borderRadius: "10px" }}>
                          <i className="fas fa-info-circle me-2"></i>
                          Withdrawals are processed within 24-48 hours. Ensure your wallet address supports {cryptoType} to avoid loss of funds.
                        </div>

                      </form>
                    </div>
                  </div>
                </div>
              </div>

            </div>
            
            <Footer />
          </div>
        </div>
      </div>

      {/* Fee Confirmation Modal */}
      {showFeeModal && pendingWithdrawal && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title fw-semibold">Confirm Withdrawal Fee</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowFeeModal(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    A processing fee will be applied to your withdrawal:
                  </p>
                  
                  <div className="bg-light rounded p-3 mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Withdrawal Amount:</span>
                      <span className="fw-semibold">${pendingWithdrawal.amount.toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Processing Fee ({withdrawalFeePercentage}%):</span>
                      <span className="text-warning fw-semibold">-${withdrawalFee.toFixed(2)}</span>
                    </div>
                    <hr className="my-2" />
                    <div className="d-flex justify-content-between">
                      <span className="fw-semibold">Total Deducted:</span>
                      <span className="fw-bold text-dark">
                        ${(pendingWithdrawal.amount + withdrawalFee).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  
                  <p className="small text-muted mb-0">
                    <i className="fas fa-shield-alt me-1"></i>
                    This fee covers network transaction costs and processing.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4"
                    onClick={() => setShowFeeModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={() => confirmWithdrawal(pendingWithdrawal)}
                    disabled={submitting}
                  >
                    {submitting ? "Confirming..." : "Confirm & Withdraw"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowFeeModal(false)}
          ></div>
        </>
      )}
    </div>
  );
}

export default Withdrawal;